import { useCallback, useEffect, useState } from "react";
import styles from "./tournament-detail.module.css";
import TournamentBanner from "./Detail/TournamentBanner";
import Rules from "./Detail/Rules";
import PrizePool from "./Detail/PrizePool";
import RegistrationPanel from "./Detail/RegistrationPanel";
import Participants from "./Detail/Participants";
import TournamentInfo from "./Detail/TournamentInfo";
import { useParams } from "react-router-dom";
import { api } from "@/utils/apiService";
import NotFound from "./Detail/NotFound";
import UpdateTournamentModal from "./Detail/UpdateTournamentModal";
import DeleteConfirmModal from "./Detail/DeleteConfirmModal";
import { toast } from "react-toastify";
import { useAuth } from "@/components/context/AuthContext";
import { useTheme } from "@/components/context/ThemeContext";
import StatusUpdateModal from "./Detail/StatusUpdateModal";
import GlobalLoader from "@/components/Loading/GlobalLoader";
import RegistrationModal from "./Detail/RegistrationModal";
import RegistrationStats from "./Detail/RegistrationStats";
import RegistrationsViewerModal from "./Detail/RegistrationsViewerModal";
import CancelRegistrationModal from "./Detail/CancelRegistrationModal";

export default function TournamentDetail() {
  const { tournamentId } = useParams();
  const { token, user } = useAuth();
  const { theme } = useTheme();

  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showRegistrationsViewer, setShowRegistrationsViewer] = useState(false);
  const [showCancelRegistrationModal, setShowCancelRegistrationModal] =
    useState(false);
  const [tournament, setTournament] = useState({});
  const [userRegistration, setUserRegistration] = useState(null);
  const [registrationStats, setRegistrationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const handleUpdate = async (updatedData) => {
    try {
      const formData = new FormData();
      formData.append("name", updatedData.name);
      formData.append("description", updatedData.description);
      formData.append("prizePool", updatedData.prizePool);
      formData.append("rules", updatedData.rules);
      formData.append("bannerImage", updatedData.bannerImage);

      const res = await api.patch(
        `/admin/tournaments/${updatedData._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.success) {
        toast.success("Tournament Updated Successfully");
        fetchTournament(tournamentId);
      }
    } catch (error) {
      console.log("Error Updating Tournament Detail ::", error);
      const errMssg =
        error?.response?.data?.message ||
        error.message ||
        "Error Updating Tournament";
      toast.error(errMssg);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/admin/tournaments/${tournamentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.success) {
        toast.success("Tournament Deleted Successfully");
        fetchTournament(tournamentId);
      }
    } catch (error) {
      console.log("Error Deleting Tournament Detail ::", error);
      const errMssg =
        error?.response?.data?.message ||
        error.message ||
        "Error Deleting Tournament";
      toast.error(errMssg);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleStatus = async (updatedStatus) => {
    try {
      const res = await api.patch(
        `/admin/tournaments/${tournamentId}/status`,
        updatedStatus,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.success) {
        const mssg =
          res.data.message || "Tournament status updated successfully";
        toast.success(mssg);
        fetchTournament(tournamentId);
      }
    } catch (error) {
      console.log("Error Updating Status");
      const mssg = error?.response?.data?.message || "Error Updating Status";
      toast.error(mssg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistration = async (registrationData) => {
    try {
      const res = await api.post(
        `/tournaments/${tournamentId}/register`,
        registrationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.success) {
        toast.success(res.data.message);

        // Handle redirect to external payment site if URL is provided
        const redirectUrl = res.data.data?.paymentUrl;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }

        fetchTournament(tournamentId);
      }
    } catch (error) {
      console.log("Error Registering For Tournament ::", error);
      const mssg =
        error?.response?.data?.message || "Error Registering For Tournament";
      toast.error(mssg);
    }
  };

  const handleCancelRegistration = async (registrationId) => {
    try {
      const res = await api.delete(
        `/tournaments/registrations/${registrationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.success) {
        const mssg = res.data.message || "Registration cancelled successfully";
        toast.success(mssg);
        fetchTournament(tournamentId);
      }
    } catch (error) {
      console.log("Error Cancelling Registration ::", error);
      const mssg =
        error?.response?.data?.message || "Error Cancelling Registration";
      toast.error(mssg);
    }
  };

  const fetchTournament = useCallback(async (id) => {
    setLoading(true);
    setErrorMessage("");
    try {
      let res;
      if (user?.role === "admin") {
        res = await api.get(`/admin/tournaments/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        res = await api.get(`/tournaments/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      console.log(res.data.data);
      setTournament(res.data.data.tournament);
      setUserRegistration(res.data.data.userRegistration);
      if (res.data.data.registrationStats) {
        setRegistrationStats(res.data.data.registrationStats);
      }
    } catch (error) {
      console.log("Error Fetching Tournament Detail ::", error);
      setErrorMessage("Sorry! No Tournament Found!");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTournament(tournamentId);
  }, [tournamentId, fetchTournament]);

  if (loading)
    return (
      <GlobalLoader
        isLoading={loading}
        variant="pulse"
        message="Preparing tournament"
        fullScreen={true}
      />
    );

  return (
    <main
      className={`${styles.container} ${theme === "dark" ? "dark" : ""} ${styles[theme]}`}
    >
      {errorMessage ? (
        <NotFound />
      ) : (
        <>
          <TournamentBanner
            tournament={tournament}
            onEditClick={() => setShowUpdateModal(true)}
            onDeleteClick={() => setShowDeleteModal(true)}
            onStatusClick={() => setShowStatusModal(true)}
          />

          <div className={styles.mainContent}>
            <div className={styles.contentGrid}>
              <div className={styles.leftColumn}>
                <TournamentInfo tournament={tournament} />
                <Rules rules={tournament?.rules} />
                <PrizePool prizePool={tournament?.prizePool} />
              </div>

              <div className={styles.rightColumn}>
                <RegistrationPanel
                  tournament={tournament}
                  userRegistration={userRegistration}
                  onRegisterClick={() => setShowRegistrationModal(true)}
                  onCancelRegistrationClick={() =>
                    setShowCancelRegistrationModal(true)
                  }
                />
                <Participants
                  currentRegistrations={tournament?.currentRegistrations}
                  maxPlayers={tournament?.maxPlayers}
                />
                {user?.role === "admin" && (
                  <RegistrationStats
                    registrationStats={registrationStats}
                    metadata={tournament?.metadata}
                    onViewRegistrations={() => setShowRegistrationsViewer(true)}
                  />
                )}
              </div>
            </div>
          </div>

          {showUpdateModal && (
            <UpdateTournamentModal
              tournament={tournament}
              onClose={() => setShowUpdateModal(false)}
              onUpdate={handleUpdate}
            />
          )}

          {showStatusModal && (
            <StatusUpdateModal
              tournament={tournament}
              onClose={() => setShowStatusModal(false)}
              onUpdate={handleStatus}
            />
          )}

          {showDeleteModal && (
            <DeleteConfirmModal
              tournament={tournament}
              onClose={() => setShowDeleteModal(false)}
              onConfirm={handleDelete}
            />
          )}

          {showRegistrationModal && (
            <RegistrationModal
              tournament={tournament}
              onClose={() => setShowRegistrationModal(false)}
              onRegister={handleRegistration}
            />
          )}

          {showRegistrationsViewer && (
            <RegistrationsViewerModal
              tournamentId={tournament._id}
              onClose={() => setShowRegistrationsViewer(false)}
            />
          )}

          {showCancelRegistrationModal && (
            <CancelRegistrationModal
              registration={userRegistration}
              tournament={tournament}
              onClose={() => setShowCancelRegistrationModal(false)}
              onConfirm={handleCancelRegistration}
            />
          )}
        </>
      )}
    </main>
  );
}
